import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchLeads, updateLead, createLead } from "@/lib/api";
import { LEAD_STAGES } from "@/lib/format";
import { KanbanBoard, KanbanCard } from "@/components/Kanban";
import FormDialog from "@/components/FormDialog";

const stageColor = (s) => {
  const m = {
    "New Lead": "bg-zinc-500/10 text-zinc-300 border-zinc-700",
    Contacted: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    "Demo Scheduled": "bg-purple-500/10 text-purple-300 border-purple-500/30",
    "Proposal Sent": "bg-amber-500/10 text-amber-300 border-amber-500/30",
    Negotiation: "bg-orange-500/10 text-orange-300 border-orange-500/30",
    "Closed Won": "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    "Closed Lost": "bg-red-500/10 text-red-300 border-red-500/30",
  };
  return m[s] || "bg-zinc-500/10 text-zinc-300 border-zinc-700";
};

const CRM = () => {
  const [leads, setLeads] = useState(null);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchLeads().then(setLeads).catch(() => setLeads([]));
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    LEAD_STAGES.forEach((s) => (out[s] = []));
    (leads || []).forEach((ld) => out[ld.stage]?.push(ld));
    return out;
  }, [leads]);

  const onMove = async (id, newStage) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage: newStage } : l)));
    try {
      await updateLead(id, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
    } catch (_e) {
      setLeads(prev);
      toast.error("Could not move");
    }
  };

  const moveStageByDir = async (id, dir) => {
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    const i = LEAD_STAGES.indexOf(lead.stage);
    const next = LEAD_STAGES[Math.max(0, Math.min(LEAD_STAGES.length - 1, i + dir))];
    if (next === lead.stage) return;
    await onMove(id, next);
    setActive((a) => (a && a.id === id ? { ...a, stage: next } : a));
  };

  const saveNotes = async () => {
    if (!active) return;
    try {
      await updateLead(active.id, { notes });
      setLeads((ls) => ls.map((l) => (l.id === active.id ? { ...l, notes } : l)));
      toast.success("Notes saved");
    } catch (_e) {
      toast.error("Could not save");
    }
  };

  if (leads === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">CRM / Leads</h1>
          <p className="text-sm text-zinc-500 mt-1">{leads.length} leads in pipeline</p>
        </div>
        <FormDialog
          testid="lead-create"
          title="Add a new lead"
          description="Capture a new brand lead directly into the pipeline."
          submitLabel="Add Lead"
          trigger={
            <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-lead-btn">
              + Add Lead
            </Button>
          }
          fields={[
            { key: "company", label: "Company", required: true, placeholder: "Plum Goodness", gridCols: 2 },
            { key: "name", label: "Contact name", required: true, placeholder: "Anita Desai", gridCols: 2 },
            { key: "email", label: "Email", required: true, placeholder: "anita@plum.in", gridCols: 2 },
            { key: "budget", label: "Budget", placeholder: "₹5L–₹10L", gridCols: 2 },
            { key: "source", label: "Source", placeholder: "Website", gridCols: 2 },
            { key: "message", label: "Notes", type: "textarea", placeholder: "Wants beauty creator reels…" },
          ]}
          onSubmit={async (form) => {
            const created = await createLead({
              name: form.name,
              email: form.email,
              company: form.company,
              budget: form.budget || "",
              message: form.message || "",
              source: form.source || "Manual",
            });
            const ld = created.lead;
            setLeads((ls) => [ld, ...(ls || [])]);
            toast.success(`Lead '${ld.company}' added`);
          }}
        />
      </div>

      <div data-testid="crm-kanban">
        <KanbanBoard
          columns={LEAD_STAGES.map((s) => ({ id: s, title: s }))}
          itemsByColumn={grouped}
          onMove={onMove}
          renderCard={(l) => (
            <KanbanCard
              key={l.id}
              id={l.id}
              onClick={() => {
                setActive(l);
                setNotes(l.notes || "");
              }}
            >
              <div className="text-sm font-medium text-zinc-100">{l.company}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{l.name}</div>
              <div className="mt-3 flex items-center justify-between text-[11px]">
                <span className="text-zinc-400">{l.budget}</span>
                <span className="text-blue-300">{l.source}</span>
              </div>
            </KanbanCard>
          )}
        />
        <p className="text-[11px] text-zinc-500 mt-3 px-1">Tip: drag a lead between columns to update its stage.</p>
      </div>

      <Sheet open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <SheetContent className="bg-zinc-950 border-l border-white/10 text-zinc-100 w-full sm:max-w-md" data-testid="lead-drawer">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="text-zinc-50">{active.company}</SheetTitle>
                <SheetDescription className="text-zinc-500">
                  {active.name} · {active.email}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">Budget</div>
                    <div className="mt-1 text-zinc-100">{active.budget}</div>
                  </div>
                  <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500">Source</div>
                    <div className="mt-1 text-zinc-100">{active.source}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Stage</div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${stageColor(active.stage)}`}>{active.stage}</span>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Notes</div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="bg-white/[0.03] border-white/10" data-testid="lead-notes" />
                </div>

                <div className="pt-2 flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={() => moveStageByDir(active.id, -1)} className="border-white/10 text-zinc-200 hover:bg-white/5" data-testid="lead-move-back">← Move back</Button>
                  <Button onClick={() => moveStageByDir(active.id, 1)} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="lead-move-forward">Advance →</Button>
                  <Button onClick={saveNotes} variant="outline" className="border-white/10 text-zinc-200 hover:bg-white/5" data-testid="lead-save-notes">Save notes</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CRM;
